import { AppointmentFilters, emptyAppointmentFilters, filterAppointments } from "../../components/AppointmentFilters";
import { AppointmentMessagingStatus } from "../../components/AppointmentMessagingStatus";
import { PublicClinicsDirectory, PublicDoctorsDirectory } from "../PublicDirectoryPages";
import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, EmptyState } from "../../components/Common";
import api from "../../lib/api";
import { Calendar, FileText, Receipt, BarChart3, Stethoscope } from "lucide-react";
import { Mascot } from "../../components/Mascot";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { printInvoiceDocument } from "../../lib/invoicePrint";
import { printPrescriptionDocument } from "../../lib/prescriptionPrint";
import { ClinicBillingWorkspace } from "../../components/ClinicBillingWorkspace";
import { ConsultationControl } from "../../components/ConsultationControl";
import { SlotRescheduleModal } from "../../components/AppointmentSlotPicker";
import { formatAppointmentDateTime } from "../../lib/branchTime";

export const links = [
  { to: "/my-account", label: "Orders & appointments" },
  { to: "/pharmacies", label: "Find pharmacies" },
  { to: "/pharmacy-orders", label: "Pharmacy orders" },
  { to: "/patient", label: "Dashboard", icon: <BarChart3 className="w-4 h-4" />, end: true },
  { to: "/patient/book", label: "Book Appointment", icon: <Calendar className="w-4 h-4" /> },
  { to: "/patient/profile", label: "My Health Record", icon: <Stethoscope className="w-4 h-4" /> },
  { to: "/patient/appointments", label: "My Appointments", icon: <Calendar className="w-4 h-4" /> },
  { to: "/patient/prescriptions", label: "Prescriptions", icon: <FileText className="w-4 h-4" /> },
  { to: "/patient/invoices", label: "Invoices", icon: <Receipt className="w-4 h-4" /> },
];

export function PatientOverview() {
  const [data, setData] = useState(null);
  const [health,setHealth]=useState(null);
  useEffect(()=>{api.get("/clinic/patients-x/me/clinical-summary").then(r=>setHealth(r.data)).catch(()=>{});},[]);
  useEffect(() => { api.get("/dashboard/patient").then((r) => setData(r.data)); }, []);
  return (
    <DashboardLayout links={links} title="Welcome to MediGenie">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard testid="pat-upcoming" label="Upcoming appointments" value={data?.upcoming_appointments ?? "–"} icon={<Calendar className="w-5 h-5 text-mg-medical" />} />
        <StatCard testid="pat-rx" label="Prescriptions" value={data?.prescriptions ?? "–"} icon={<FileText className="w-5 h-5 text-mg-genie" />} />
        <StatCard testid="pat-pending" label="Pending invoices" value={data?.pending_invoices ?? "–"} icon={<Receipt className="w-5 h-5 text-mg-gold" />} />
        <StatCard testid="pat-followups" label="Follow-ups" value={data?.followups_pending ?? "–"} icon={<Stethoscope className="w-5 h-5 text-mg-health" />} />
      </div>
      {data?.patient_profile && <div className="grid md:grid-cols-2 gap-5 mt-6"><section className="mg-card"><h2 className="font-bold text-lg">My profile</h2><p className="mt-3 font-semibold">{data.patient_profile.name}</p><p>{data.patient_profile.gender} · {data.patient_profile.date_of_birth||data.patient_profile.dob||"Date of birth not recorded"}</p><p>{data.patient_profile.phone}</p><p>{data.patient_profile.email}</p><Link to="/patient/profile" className="text-mg-medical">View health record</Link></section><section className="mg-card"><h2 className="font-bold text-lg">Health details</h2><p className="mt-3">Blood group: {data.patient_profile.blood_group||"Not recorded"}</p><p>Allergies: {data.patient_profile.allergies||data.patient_profile.allergies_json||"Not recorded"}</p><p>Medical history: {data.patient_profile.medical_history_text||"Not recorded"}</p>{health?.latest_vitals&&<p className="mt-2">Latest vitals: BP {health.latest_vitals.blood_pressure||"—"}, pulse {health.latest_vitals.pulse||"—"}, weight {health.latest_vitals.weight||"—"} kg</p>}<p className="mt-2">Medicines: {(health?.current_medications||[]).map(m=>m.medicine_name).join(", ")||"None recorded"}</p></section></div>}
      {!data?.patient_profile && (
        <div className="mg-card mt-6 flex items-center gap-6 mg-gradient-soft">
          <Mascot size={80} className="mg-float" />
          <div>
            <h3 className="font-bold text-mg-navy text-lg">No clinic linked yet</h3>
            <p className="text-sm text-slate-600 mt-1">Choose an active MediGenie clinic to start your health journey. <Link className="text-mg-medical font-semibold" to="/patient/book">Find a clinic</Link>.</p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function PatientAppointments() {
  const [filters,setFilters]=useState(emptyAppointmentFilters);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const refresh = () => { setLoading(true); setError(false); return api.get("/appointments").then((r) => setList(r.data)).catch(() => setError(true)).finally(() => setLoading(false)); };
  useEffect(() => { refresh(); }, []);
  const cancel = async (a) => {
    const reason = window.prompt("Why are you cancelling this appointment?", "Patient requested cancellation");
    if (reason === null) return;
    try { await api.post(`/appointments/${a.appointment_id}/patient-cancel`, { reason }); toast.success("Appointment cancelled"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel appointment"); }
  };
  const reschedule = async (next, reason) => {
    setRescheduleBusy(true);
    try { await api.post(`/appointments/${rescheduleFor.appointment_id}/patient-reschedule`, { new_datetime: next, reason: reason || "Patient requested reschedule" }); toast.success("Appointment rescheduled"); setRescheduleFor(null); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule appointment"); }
    finally { setRescheduleBusy(false); }
  };

  const exportCsv = () => {
    if (!list.length) return toast.error("No data");
    const headers = ["Date", "Doctor", "Service", "Status"];
    const csv = [headers.join(","), ...filterAppointments(list,filters).map(a => `"${formatAppointmentDateTime(a)}","${a.doctor?.name || ''}","${a.service?.name || ''}","${a.status}"`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `my-appointments-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <DashboardLayout links={links} title="My Appointments">
      <AppointmentFilters rows={list} value={filters} onChange={setFilters}/><div className="flex justify-end mb-4">
        <button onClick={exportCsv} className="mg-btn-secondary text-sm">Download Report</button>
      </div>
      <div className="mg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">When</th><th>Clinic</th><th>Doctor</th><th>Service</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filterAppointments(list,filters).map((a) => (
              <tr key={a.appointment_id} className="border-b border-slate-50" data-testid={`pat-appt-${a.appointment_id}`}>
                <td className="py-3 font-semibold">{formatAppointmentDateTime(a)}</td>
                <td>{a.clinic?.name}<div className="text-xs text-slate-500">{a.clinic?.address}</div></td><td>{a.doctor?.name}<div className="text-xs text-slate-500">{a.doctor?.specialization}</div></td>
                <td>{a.service?.name}</td>
                <td><span className={`mg-status mg-status-${a.status.toLowerCase().replace(/\s+/g, "-")}`}>{a.status}</span><AppointmentMessagingStatus delivery={a.notification_delivery} /></td>
                <td className="space-x-2">{a.consultation_mode === "online" && ["Scheduled", "Confirmed", "Checked In", "In Consultation"].includes(a.status) && <ConsultationControl appointmentId={a.appointment_id}/>} {a.consultation_mode === "in_person" && <span className="text-xs text-slate-500">In-person</span>}{["Scheduled", "Confirmed"].includes(a.status) && <><button className="text-xs text-mg-medical font-semibold" onClick={() => setRescheduleFor(a)}>Reschedule</button><button className="text-xs text-red-600 font-semibold" onClick={() => cancel(a)}>Cancel</button></>}</td>
              </tr>
            ))}
            {loading && <tr><td colSpan="6" className="py-8 text-center text-slate-400 text-sm">Loading appointments…</td></tr>}
            {!loading && error && <tr><td colSpan="6" className="py-8 text-center text-red-600 text-sm">Appointments could not be loaded. Please try again.</td></tr>}
            {!loading && !error && filterAppointments(list,filters).length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-400 text-sm">No appointments yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {rescheduleFor && <SlotRescheduleModal appointment={rescheduleFor} onClose={() => setRescheduleFor(null)} onSubmit={reschedule} busy={rescheduleBusy} />}
    </DashboardLayout>
  );
}

export function PatientPrescriptions() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = () => { setLoading(true); setError(""); api.get("/clinic/patients-x/me/prescriptions").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => setError(e?.response?.data?.detail || "Prescriptions could not be loaded.")).finally(() => setLoading(false)); };
  useEffect(load, []);
  return (
    <DashboardLayout links={links} title="Prescriptions">
      <div className="space-y-4">
        {loading && <div className="mg-card text-center text-slate-500" role="status">Loading prescriptions…</div>}
        {error && <div className="mg-card text-center" role="alert"><p className="text-red-600 mb-3">{error}</p><button className="mg-btn-secondary" onClick={load}>Retry</button></div>}
        {!loading && !error && list.map((p) => (
          <div key={p.prescription_id || p.id} className="mg-card" data-testid={`pat-rx-${p.prescription_id || p.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-mg-navy">{p.doctor_name || p.doctor?.name || "Doctor"}</div>
                <div className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString()}</div>
                {p.clinic_name && <div className="text-xs text-slate-500">{p.clinic_name}</div>}
              </div>
              <button type="button" className="mg-btn-secondary text-xs" data-testid={`pat-rx-print-${p.prescription_id || p.id}`} onClick={() => { try { printPrescriptionDocument(p); } catch (e) { toast.error(e.message || "Please allow pop-ups to print your prescription."); } }}>Print / Save PDF</button>
            </div>
            <div className="mt-3 space-y-1.5">
              {(p.items || p.medicines || []).map((it, i) => (
                <div key={i} className="text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className="font-semibold text-mg-navy">{it.medicine_name}</span> {it.strength && <span className="text-slate-500">({it.strength})</span>}
                  <div className="text-xs text-slate-600">{it.dosage} • {it.frequency} • {it.duration}</div>
                  {it.instructions && <div className="text-xs text-slate-500">{it.instructions}</div>}
                </div>
              ))}
            </div>
            {(p.notes || p.prescription_notes) && <div className="text-xs text-slate-500 mt-3">Notes: {p.notes || p.prescription_notes}</div>}
            <div className="text-[10px] text-slate-400 mt-3 italic">Always follow your doctor's instructions. MediGenie is not a substitute for professional medical advice.</div>
          </div>
        ))}
        {!loading && !error && list.length === 0 && <EmptyState title="No prescriptions yet" message="Your prescriptions will appear here after consultations." />}
      </div>
    </DashboardLayout>
  );
}

export function PatientInvoices() {
  return <DashboardLayout links={links} title="My Invoices"><ClinicBillingWorkspace /></DashboardLayout>;
}

function LegacyPatientInvoices() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/billing/invoices").then((r) => setList(r.data)); }, []);
  
  const legacyPrintInvoice = (inv) => {
     // A simple print logic: open a new window, write invoice details, and call print()
     const printWindow = window.open("", "_blank");
     printWindow.document.write(`
       <html><head><title>Invoice ${inv.invoice_number}</title></head><body style="font-family: sans-serif; padding: 40px;">
       <h2>Invoice: ${inv.invoice_number}</h2>
       <p>Date: ${new Date(inv.invoice_date).toLocaleDateString()}</p>
       <p>Status: <strong>${inv.status}</strong></p>
       <hr/>
       <table width="100%" border="1" cellspacing="0" cellpadding="8">
         <tr><th>Item</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
         ${inv.items.map(it => `<tr><td>${it.description}</td><td>${it.quantity}</td><td>${it.unit_price}</td><td>${it.total}</td></tr>`).join('')}
       </table>
       <br/>
       <p><strong>Subtotal:</strong> ₹${inv.subtotal}</p>
       <p><strong>Discount:</strong> ₹${inv.discount}</p>
       <p><strong>Tax:</strong> ₹${inv.tax_amount}</p>
       <p><strong>Total:</strong> ₹${inv.total}</p>
       <p><strong>Paid Amount:</strong> ₹${inv.paid_amount}</p>
       <p><strong>Balance Amount:</strong> ₹${inv.balance_amount}</p>
       <script>window.print(); window.close();</script>
       </body></html>
     `);
     printWindow.document.close();
  };
  const printInvoice = (inv) => {
    try { printInvoiceDocument(inv); } catch (error) { toast.error(error.message); }
  };

  const exportCsv = () => {
    if (!list.length) return toast.error("No data");
    const headers = ["Invoice No", "Date", "Total", "Balance", "Status"];
    const csv = [headers.join(","), ...list.map(i => `"${i.invoice_number}","${new Date(i.invoice_date).toLocaleDateString()}","${i.total}","${i.balance_amount}","${i.status}"`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `my-invoices-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <DashboardLayout links={links} title="My Invoices">
      <div className="flex justify-end mb-4">
        <button onClick={exportCsv} className="mg-btn-secondary text-sm">Download Report</button>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Invoice</th><th>Date</th><th className="text-right">Total</th><th className="text-right">Balance</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.invoice_id} className="border-b border-slate-50">
                <td className="py-3 font-semibold text-mg-navy">{i.invoice_number}</td>
                <td className="text-xs text-slate-500">{new Date(i.invoice_date).toLocaleDateString()}</td>
                <td className="text-right font-semibold">₹{i.total}</td>
                <td className="text-right text-mg-gold font-semibold">₹{i.balance_amount}</td>
                <td><span className={`mg-status mg-status-${i.status.toLowerCase()}`}>{i.status}</span></td>
                <td>
                  <button onClick={() => printInvoice(i)} className="text-mg-medical text-xs font-semibold hover:underline">Print / Save PDF</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-400 text-sm">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function PatientEncounters() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get("/encounters").then((r) => {
      setList(r.data);
      setLoading(false);
    });
  }, []);

  const exportCsv = () => {
    if (!list.length) return toast.error("No data");
    const headers = ["Date", "Doctor", "Complaint", "Status"];
    const csv = [headers.join(","), ...list.map(e => `"${new Date(e.encounter_datetime).toLocaleDateString()}","${e.doctor?.name || ''}","${e.chief_complaint || ''}","${e.status}"`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `my-encounters-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <DashboardLayout links={links} title="My Encounters">
      <div className="flex justify-end mb-4">
        <button onClick={exportCsv} className="mg-btn-secondary text-sm">Download Report</button>
      </div>
      <div className="mg-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="p-4">Date</th>
              <th>Doctor</th>
              <th>Chief Complaint</th>
              <th>Status</th>
              <th className="text-right pr-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan="5" className="p-8 text-center text-slate-400">Loading...</td></tr>}
            {!loading && list.map(e => (
              <tr key={e.encounter_id} className="hover:bg-slate-50">
                <td className="p-4 font-semibold text-mg-navy">{new Date(e.encounter_datetime).toLocaleDateString()}</td>
                <td>{/^dr\.?\s/i.test(e.doctor?.name || "") ? e.doctor?.name : `Dr. ${e.doctor?.name || "—"}`}</td>
                <td className="text-slate-600">{e.chief_complaint || "Routine Consultation"}</td>
                <td><span className={`mg-status mg-status-${e.status?.toLowerCase().replace(" ","-")}`}>{e.status}</span></td>
                <td className="text-right pr-4">
                   <Link to={`/patient/encounters/${e.encounter_id}`} className="mg-btn-secondary py-1 text-xs">View Details</Link>
                </td>
              </tr>
            ))}
            {!loading && list.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-slate-400">No encounters found.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function PatientBookAppointment() {
  const [tab,setTab]=useState("clinics");
  return <DashboardLayout links={links} title="Find doctors & clinics"><div className="flex gap-3 mb-4"><button className="mg-btn-secondary" aria-pressed={tab==="clinics"} onClick={()=>setTab("clinics")}>Find clinics</button><button className="mg-btn-secondary" aria-pressed={tab==="doctors"} onClick={()=>setTab("doctors")}>Find doctors</button></div>{tab==="clinics"?<PublicClinicsDirectory embedded/>:<PublicDoctorsDirectory embedded/>}</DashboardLayout>;
}

